import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { MarketplaceProduct } from '@isbd/shared';
import { MARKETPLACE_PLACEHOLDER_PHOTO } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { MarketingNav } from '@/pages/marketing/MarketingNav';
import { MarketingFooter } from '@/pages/marketing/MarketingFooter';
import '@/pages/marketing/marketing.css';
import { ContactSellerDialog } from '@/features/marketplace/ContactSellerDialog';
import { SellerProducts } from '@/features/marketplace/SellerProducts';
import { normalizeProduct, stars } from '@/features/marketplace/normalize';
import '@/features/marketplace/marketplace.css';

function formatPrice(value: number, currency: string) {
  return `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}${currency}`;
}

export function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState<MarketplaceProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) {
        setProduct(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setActivePhoto(0);
      const { data, error } = await supabase
        .from('marketplace_products')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (cancelled) return;
      if (error || !data) {
        setProduct(null);
      } else {
        setProduct(normalizeProduct(data as Record<string, unknown>));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="mkt mkt-page">
        <MarketingNav />
        <div className="mp-detail">
          <div className="mp-detail-main">
            <div className="mp-skeleton">
              <div className="mp-skeleton-media" />
            </div>
            <div>
              <div className="mp-skeleton-line" />
              <div className="mp-skeleton-line short" />
              <div className="mp-skeleton-line" style={{ height: '6rem', marginTop: '1rem' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mkt mkt-page">
        <MarketingNav />
        <div className="mp-detail" style={{ textAlign: 'center' }}>
          <p className="muted">Produit introuvable.</p>
          <Link to="/marketplace" className="mp-back">
            ← Retour à la marketplace
          </Link>
        </div>
      </div>
    );
  }

  const currency = product.currency || '€';
  const photos =
    product.photo_urls?.length > 0 ? product.photo_urls : [MARKETPLACE_PLACEHOLDER_PHOTO];
  const videoEmbed = product.video_url || '';

  return (
    <div className="mkt mkt-page">
      <MarketingNav />
      <div className="mp-detail">
        <Link to="/marketplace" className="mp-back">
          ← Retour à la marketplace
        </Link>

        <div className="mp-detail-main">
          <div>
            <div className="mp-gallery-main">
              <img src={photos[activePhoto]} alt={product.name} />
              {product.is_premium ? (
                <span className="mp-card-badge" style={{ top: '0.75rem', left: '0.75rem' }}>
                  ★ Premium
                </span>
              ) : null}
            </div>
            {photos.length > 1 ? (
              <div className="mp-gallery-thumbs">
                {photos.map((url, i) => (
                  <button
                    key={`${url}-${i}`}
                    type="button"
                    className={activePhoto === i ? 'is-active' : undefined}
                    onClick={() => setActivePhoto(i)}
                    aria-label={`Photo ${i + 1}`}
                  >
                    <img src={url} alt="" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="mp-detail-info">
            <div className="mp-detail-category">{product.category}</div>
            <h1>{product.name}</h1>

            {product.rating > 0 ? (
              <div className="mp-detail-rating">
                <span className="mp-stars" aria-hidden>
                  {stars(product.rating).map((s, i) => (
                    <span key={i} className={s === '☆' ? 'off' : undefined}>
                      {s === '☆' ? '★' : s}
                    </span>
                  ))}
                </span>
                <strong>{product.rating}</strong>
              </div>
            ) : null}

            <div className="mp-detail-price">
              <strong>{formatPrice(product.price, currency)}</strong>
              {product.old_price ? <s>{formatPrice(product.old_price, currency)}</s> : null}
            </div>

            <div className="mp-seller-row">
              {product.seller_logo ? (
                <img
                  className="mp-seller-avatar"
                  src={product.seller_logo}
                  alt={product.seller_name}
                />
              ) : (
                <div className="mp-seller-avatar" aria-hidden>
                  {product.seller_name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div>
                <strong>{product.seller_name}</strong>
                <span>Vendeur vérifié</span>
              </div>
            </div>

            <div className="mp-detail-desc">
              <h2>Description</h2>
              <p>{product.description}</p>
            </div>

            <button
              type="button"
              className="btn btn-primary mp-detail-contact"
              onClick={() => setContactOpen(true)}
            >
              Contacter le vendeur
            </button>

            <ContactSellerDialog
              product={product}
              open={contactOpen}
              onOpenChange={setContactOpen}
            />
          </div>
        </div>

        {videoEmbed ? (
          <div className="mp-section">
            <h2>Vidéo d&apos;illustration</h2>
            <div className="mp-video">
              <iframe
                src={videoEmbed}
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title="Vidéo d'illustration"
              />
            </div>
          </div>
        ) : null}

        {product.testimonials.length > 0 ? (
          <div className="mp-section">
            <h2>Témoignages</h2>
            <div className="mp-testimonials">
              {product.testimonials.map((t, i) => (
                <div key={`${t.author}-${i}`} className="mp-testimonial">
                  <span className="mp-testimonial-quote" aria-hidden>
                    “
                  </span>
                  <span className="mp-stars" aria-hidden>
                    {stars(t.rating || 5).map((s, j) => (
                      <span key={j} className={s === '☆' ? 'off' : undefined}>
                        {s === '☆' ? '★' : s}
                      </span>
                    ))}
                  </span>
                  <p>&ldquo;{t.text}&rdquo;</p>
                  <div className="mp-testimonial-author">
                    <div className="mp-seller-avatar" aria-hidden>
                      {t.author?.[0]?.toUpperCase() || '?'}
                    </div>
                    <strong>{t.author}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <SellerProducts
          sellerName={product.seller_name}
          ownerId={product.owner_id}
          excludeId={product.id}
        />
      </div>
      <MarketingFooter />
    </div>
  );
}
